import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from "react";

/**
 * WebSocket Context for managing real-time messages
 * Uses useReducer for better state management
 */

const WebSocketContext = createContext();

// Action types
export const WS_ACTIONS = {
  CONNECT: "CONNECT",
  DISCONNECT: "DISCONNECT",
  REGISTER_USER: "REGISTER_USER",
  USER_ONLINE: "USER_ONLINE",
  USER_OFFLINE: "USER_OFFLINE",
  RECEIVE_MESSAGE: "RECEIVE_MESSAGE",
  SEND_MESSAGE: "SEND_MESSAGE",
  ADD_MESSAGE: "ADD_MESSAGE",
  CLEAR_MESSAGES: "CLEAR_MESSAGES",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  CONNECTED_USERS: "CONNECTED_USERS",
};

// Initial state
const initialState = {
  socket: null,
  isConnected: false,

  isLoading: false,
  error: null,
  userId: null,
  messages: [], // Array of messages
  messagesByUser: {}, // Object to group messages by user
  connectedUsers: [], // List of online users
  connectionAttempts: 0,
};

/**
 * Reducer function for WebSocket state management
 */
const wsReducer = (state, action) => {
  switch (action.type) {
    case WS_ACTIONS.CONNECT:
      return {
        ...state,
        socket: action.payload.socket,
        isConnected: true,
        isLoading: false,
        error: null,
        connectionAttempts: 0,
      };

    case WS_ACTIONS.DISCONNECT:
      return {
        ...state,
        socket: null,
        isConnected: false,
      };

    case WS_ACTIONS.REGISTER_USER:
      return {
        ...state,
        userId: action.payload.userId,
        isConnected: true,
      };

    case WS_ACTIONS.USER_ONLINE:
      return {
        ...state,
        connectedUsers: [
          ...new Set([...state.connectedUsers, action.payload.userId]),
        ],
      };

    case WS_ACTIONS.USER_OFFLINE: {
      const userId = action.payload.userId;
      return {
        ...state,
        connectedUsers: state.connectedUsers.filter((id) => id !== userId),
      };
    }

    case WS_ACTIONS.ADD_MESSAGE: {
      const { from, to, content, type, timestamp } = action.payload;
      const newMessage = {
        id: `${Date.now()}-${Math.random()}`,
        from,
        to,
        content,
        type,
        timestamp,
      };

      // Add to messages array
      const updatedMessages = [...state.messages, newMessage];

      // Add to messagesByUser
      const senderKey = from || "system";
      const updatedMessagesByUser = { ...state.messagesByUser };
      if (!updatedMessagesByUser[senderKey]) {
        updatedMessagesByUser[senderKey] = [];
      }
      updatedMessagesByUser[senderKey].push(newMessage);

      return {
        ...state,
        messages: updatedMessages,
        messagesByUser: updatedMessagesByUser,
      };
    }

    case WS_ACTIONS.CLEAR_MESSAGES:
      return {
        ...state,
        messages: [],
        messagesByUser: {},
      };

    case WS_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case WS_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case WS_ACTIONS.CONNECTED_USERS:
      return {
        ...state,
        connectedUsers: action.payload,
      };

    default:
      return state;
  }
};

/**
 * WebSocket Provider Component
 */
export const WebSocketProvider = ({ children, serverUrl = null }) => {
  const [state, dispatch] = useReducer(wsReducer, initialState);

  useEffect(() => {
    // Determine server URL - use port 3001 for WebSocket server
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.hostname;
    const port = 3001;
    const wsUrl = `${protocol}//${host}:${port}`;

    const connectWebSocket = async () => {
      try {
        dispatch({ type: WS_ACTIONS.SET_LOADING, payload: true });

        // Create WebSocket connection using native WebSocket API
        const socket = new WebSocket(wsUrl);

        // Store socket ID (will be assigned by server)
        let socketId = null;

        socket.onopen = () => {
          const timestamp = new Date().toISOString();
          console.log(
            `✅ [Frontend Connect] WebSocket connected to ${wsUrl} at ${timestamp}`,
          );
          dispatch({
            type: WS_ACTIONS.CONNECT,
            payload: { socket },
          });
        };

        socket.onclose = () => {
          const timestamp = new Date().toISOString();
          console.log(
            `❌ [Frontend Disconnect] WebSocket disconnected at ${timestamp}`,
          );
          dispatch({ type: WS_ACTIONS.DISCONNECT });
        };

        socket.onmessage = (event) => {
          try {
            const timestamp = new Date().toISOString();
            const message = JSON.parse(event.data);

            // Log all messages for debugging
            console.log(`📨 [WS Message] ${message.type} at ${timestamp}`);

            // Route messages based on type
            if (message.type === "ping") {
              console.log(`   ⏰ Responding to ping...`);
              // Respond with pong
              const pongMessage = JSON.stringify({ type: "pong" });
              socket.send(pongMessage);
              console.log(`   ✅ Pong sent`);
            } else if (message.type === "userRegistered") {
              const timestamp = new Date().toISOString();
              console.log(
                `✅ [User Registered] Backend confirmed registration at ${timestamp}`,
              );
              console.log(
                `   Device: ${message.deviceId} (${message.devices} total)`,
              );
              dispatch({
                type: WS_ACTIONS.REGISTER_USER,
                payload: { userId: message.userId, deviceId: message.deviceId },
              });
            }
            //  else if (message.type === "userOnline") {
            //   console.log(`👤 [User Online] - User: ${message.userId}`);
            //   dispatch({
            //     type: WS_ACTIONS.USER_ONLINE,
            //     payload: { userId: message.userId },
            //   });
            // }
            else if (message.type === "userOffline") {
              console.log(`👤 [User Offline] - User: ${message.userId}`);
              dispatch({
                type: WS_ACTIONS.USER_OFFLINE,
                payload: { userId: message.userId },
              });
            } else if (message.type === "message") {
              console.log(
                `💬 [Message] From: ${message.from}, Type: ${message.messageType}`,
              );
              dispatch({
                type: WS_ACTIONS.ADD_MESSAGE,
                payload: message,
              });
            }
          } catch (error) {
            console.error(`⚠️ [WS Parse Error] ${error.message}`);
          }
        };

        socket.onerror = (error) => {
          console.error(`⚠️ [WebSocket Error] Connection error:`, error);
          dispatch({
            type: WS_ACTIONS.SET_ERROR,
            payload: "WebSocket connection error",
          });
        };
      } catch (error) {
        console.error("❌ [WebSocket Init] Connection error:", error);
        dispatch({
          type: WS_ACTIONS.SET_ERROR,
          payload: error.message,
        });
      }
    };

    connectWebSocket();

    // Cleanup on unmount
    return () => {
      if (state.socket) {
        state.socket.close();
      }
    };
  }, []);

  // Memoize registerUser to prevent unnecessary re-renders in dependent components
  const registerUser = useCallback(
    (userId, deviceId) => {
      const timestamp = new Date().toISOString();
      if (state.socket && state.socket.readyState === WebSocket.OPEN) {
        console.log(
          `📱 [Register User] UserID: ${userId}, Device: ${deviceId || "auto"}`,
        );

        // Send registerUser message in JSON format
        const message = JSON.stringify({
          type: "registerUser",
          userId,
          deviceId: deviceId || undefined,
        });
        state.socket.send(message);
        console.log(`   ✅ Registration message sent`);

        dispatch({
          type: WS_ACTIONS.REGISTER_USER,
          payload: { userId, deviceId },
        });
      } else {
        console.warn(
          `⚠️ [Register User] WebSocket not ready (Status: ${state.socket?.readyState})`,
        );
      }
    },
    [state.socket],
  );

  // Memoize sendMessage to prevent unnecessary re-renders
  const sendMessage = useCallback(
    (message) => {
      if (state.socket && state.socket.readyState === WebSocket.OPEN) {
        const jsonMessage = JSON.stringify({
          type: "message",
          ...message,
          timestamp: new Date().toISOString(),
        });
        state.socket.send(jsonMessage);

        // Add to local state immediately for better UX
        dispatch({
          type: WS_ACTIONS.ADD_MESSAGE,
          payload: {
            ...message,
            timestamp: new Date().toISOString(),
          },
        });
      } else {
        console.warn(
          "⚠️ [Send Message] WebSocket not connected. Cannot send message.",
        );
      }
    },
    [state.socket],
  );

  const getConnectionStatus = useCallback(
    () => ({
      isConnected: state.isConnected,
      isLoading: state.isLoading,
      error: state.error,
      userId: state.userId,
      connectedUsersCount: state.connectedUsers.length,
    }),
    [
      state.isConnected,
      state.isLoading,
      state.error,
      state.userId,
      state.connectedUsers.length,
    ],
  );

  // Helper functions to expose
  const value = {
    state,
    dispatch,

    // Connection methods
    registerUser,

    // Message methods
    sendMessage,

    // Getter methods
    getMessages: () => state.messages,
    getMessagesByUser: (userId) => state.messagesByUser[userId] || [],
    getConnectedUsers: () => state.connectedUsers,
    // isUserOnline: (userId) => state.connectedUsers.includes(userId),

    // Utility methods
    clearMessages: () => {
      dispatch({ type: WS_ACTIONS.CLEAR_MESSAGES });
    },

    getConnectionStatus,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Custom hook to use WebSocket context
 */
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);

  if (!context) {
    throw new Error("useWebSocket must be used within WebSocketProvider");
  }

  return context;
};

export default WebSocketContext;
